/*
 * Parses a typed shell line into a command + resolved option values, mirroring spring-shell
 * option syntax (`--name value`, `--name=value`, `--flag`). Validates against the manifest.
 */
import {matchCommand, ShellCommand, ShellOption} from "./ShellManifest";

export interface ParseResult {
    command?: ShellCommand;
    /** effective option values (provided values merged over defaults); lists are string[] */
    values: Record<string, string | string[]>;
    /** options the user explicitly provided (for query inclusion of optionals) */
    provided: Set<string>;
    error?: string;
}

/** Split a line into tokens, honouring single and double quotes. */
export function tokenize(line: string): string[] {
    const tokens: string[] = [];
    const re = /"([^"]*)"|'([^']*)'|(\S+)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
        tokens.push(m[1] ?? m[2] ?? m[3]);
    }
    return tokens;
}

function findOption(command: ShellCommand, name: string): ShellOption | undefined {
    return command.options.find(o => o.name === name);
}

// spring-shell matches enum values leniently: case-insensitive, hyphen == underscore
// (so `match-all`, `match_all` and `MATCH_ALL` all resolve to the MATCH_ALL constant)
function enumKey(s: string): string {
    return s.toUpperCase().replace(/-/g, "_");
}

export function parse(line: string): ParseResult {
    const tokens = tokenize(line.trim());
    const values: Record<string, string | string[]> = {};
    const provided = new Set<string>();

    if (tokens.length === 0) {
        return {values, provided, error: "empty command"};
    }

    const match = matchCommand(tokens);
    if (!match) {
        return {values, provided, error: `unknown command: ${tokens.join(" ")}`};
    }
    const {command, consumed} = match;
    const rest = tokens.slice(consumed);

    for (let i = 0; i < rest.length; i++) {
        const tok = rest[i];
        if (!tok.startsWith("--")) {
            return {command, values, provided, error: `unexpected argument: ${tok}`};
        }
        let name = tok.slice(2);
        let value: string | undefined;
        const eq = name.indexOf("=");
        if (eq >= 0) {
            value = name.slice(eq + 1);
            name = name.slice(0, eq);
        }
        const opt = findOption(command, name);
        if (!opt) {
            return {command, values, provided, error: `unknown option --${name}`};
        }
        if (opt.type === "file") {
            // the file is chosen via the browser picker at execution time; ignore a typed value
            if (value === undefined && i + 1 < rest.length && !rest[i + 1].startsWith("--")) {
                i++;
            }
            continue;
        }
        if (opt.type === "boolean") {
            // `--flag` means true; `--flag false` / `--flag=false` allowed
            if (value === undefined && i + 1 < rest.length && /^(true|false)$/i.test(rest[i + 1])) {
                value = rest[++i];
            }
            values[name] = value === undefined ? "true" : String(/^true$/i.test(value));
        } else if (opt.type === "list") {
            // collect `--opt a b c`, `--opt a --opt b` (repeated) and `--opt=a,b`
            const collected: string[] = [];
            if (value !== undefined) {
                collected.push(...value.split(",").filter(v => v !== ""));
            } else {
                while (i + 1 < rest.length && !rest[i + 1].startsWith("--")) {
                    collected.push(...rest[++i].split(",").filter(v => v !== ""));
                }
                if (collected.length === 0) {
                    return {command, values, provided, error: `missing value for --${name}`};
                }
            }
            const prev = values[name];
            values[name] = Array.isArray(prev) ? [...prev, ...collected] : collected;
        } else {
            if (value === undefined) {
                if (i + 1 >= rest.length) {
                    return {command, values, provided, error: `missing value for --${name}`};
                }
                value = rest[++i];
            }
            values[name] = value;
        }
        provided.add(name);
    }

    // merge defaults for options the user didn't provide
    for (const opt of command.options) {
        if (!(opt.name in values) && opt.default !== undefined) {
            values[opt.name] = opt.default;
        }
    }

    // canonicalise enum values to the exact constant. Spring Shell accepts enums
    // case-insensitively and sends the canonical name to the backend, whose query->enum binding
    // is case-sensitive; mirror that so `--sort-direction asc` works like the CLI.
    for (const opt of command.options) {
        const v = values[opt.name];
        if (opt.type === "enum" && opt.enumValues && typeof v === "string") {
            const match = opt.enumValues.find(e => enumKey(e) === enumKey(v));
            if (match) {
                values[opt.name] = match;
            }
        }
    }

    const error = validate(command, values);
    return {command, values, provided, error};
}

function validate(command: ShellCommand, values: Record<string, string | string[]>): string | undefined {
    for (const opt of command.options) {
        if (opt.type === "file") {
            continue;  // supplied via the file picker, not validated here
        }
        const v = values[opt.name];
        if (v === undefined || (Array.isArray(v) && v.length === 0)) {
            if (opt.required) {
                return `missing required option --${opt.name}`;
            }
            continue;
        }
        if (typeof v === "string") {
            if ((opt.type === "int" || opt.type === "long") && !/^-?\d+$/.test(v)) {
                return `option --${opt.name} must be an integer`;
            }
            if (opt.type === "enum" && opt.enumValues && opt.enumValues.length > 0) {
                const ok = opt.enumValues.some(e => enumKey(e) === enumKey(v));
                if (!ok) {
                    return `option --${opt.name} must be one of: ${opt.enumValues.join(", ")}`;
                }
            }
        }
    }
    return undefined;
}
